/**
 * A mensagem automática que abre a conversa no chat do site.
 *
 * ## O que estava errado
 *
 * O texto era CHUMBADO aqui no clientweb -- "Olá, esta é uma mensagem
 * automática..." --, igual para todo mundo. A aba Mensagens do painel já
 * deixava o administrador escrever uma saudação POR DEPARTAMENTO, e ela nunca
 * chegava a este canal: quem entrava pelo Financeiro lia a mesma frase de
 * quem entrava pelo Suporte (pedido da Gisele, 19/10/2026).
 *
 * ## De onde vem
 *
 * De `GET /groups/hours/{id}`, o mesmo pedido que o chat já faz ao entrar
 * para saber se está no horário -- `mensagem_de_boas_vindas`. Sem ida extra
 * ao servidor bem na hora em que a pessoa está esperando a tela abrir.
 *
 * ## O texto antigo não some: vira o padrão
 *
 * Departamento sem saudação configurada (e instalação sem seletor de
 * departamento nenhum) continua com a frase de sempre. Abrir a conversa MUDA
 * seria pior que a frase genérica: a pessoa entra, não vê nada acontecer, e
 * não sabe se o chat funciona.
 */

/** A frase que o chat usava antes de haver configuração por departamento. */
export const SAUDACAO_PADRAO =
    'Olá, esta é uma mensagem automática. Caso queira, deixe uma mensagem para quando um atendente entrar em contato já estar ciente do que precisa ser feito.';

/** Onde a saudação do departamento fica entre a tela de entrada e o chat. */
export const CHAVE_DA_SAUDACAO = 'welcomeMessage';

/**
 * O que sobra tirando as etiquetas de HTML.
 *
 * A saudação é escrita num editor rico e chega como HTML ("<p>Bem-vindo!</p>"
 * é o que o banco tem hoje). Apagar o texto nesse editor NÃO deixa o campo
 * vazio: deixa "<p></p>" ou "<p><br></p>", que são "configurados" para
 * qualquer comparação com string vazia -- e o chat abriria mudo, que é
 * justamente o que não pode acontecer.
 */
const textoVisivel = (html) => String(html)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();

/**
 * A saudação a enviar.
 *
 * Texto em branco conta como "não configurado": `''`, `'   '` e o HTML sem
 * conteúdo visível são o que o banco tem de verdade quando o administrador
 * apaga o campo e salva. Tratar só o `null` deixaria o chat abrir mudo
 * exatamente nesses casos.
 *
 * O que volta é o HTML COMO VEIO (só sem espaço nas pontas): a conversa
 * renderiza HTML já sanitizado (`utils/safeHtml`), e tirar as etiquetas aqui
 * jogaria fora a formatação que o administrador escolheu.
 *
 * @param {string|null|undefined} doDepartamento
 */
export const saudacaoDoChat = (doDepartamento) => {
    if (typeof doDepartamento !== 'string') return SAUDACAO_PADRAO;

    const texto = doDepartamento.trim();

    return textoVisivel(texto) === '' ? SAUDACAO_PADRAO : texto;
};

/**
 * Guarda a saudação do departamento escolhido, para o chat ler ao abrir.
 *
 * Pelo `localStorage` porque é o caminho que o resto desta tela já usa entre
 * a entrada e o chat (`welcomeMessageSent`, `userInput`, `user`), e porque os
 * dois componentes não têm estado em comum. Falha de armazenamento (aba
 * anônima, cota cheia) não pode derrubar a entrada no chat: o pior caso é a
 * saudação padrão.
 */
export const guardarSaudacao = (texto) => {
    try {
        if (typeof texto === 'string' && textoVisivel(texto) !== '') {
            localStorage.setItem(CHAVE_DA_SAUDACAO, texto.trim());
        } else {
            localStorage.removeItem(CHAVE_DA_SAUDACAO);
        }
    } catch {
        // Sem armazenamento, cai na saudação padrão -- e o chat abre igual.
    }
};

/** A saudação guardada, ou a padrão. */
export const saudacaoGuardada = () => {
    try {
        return saudacaoDoChat(localStorage.getItem(CHAVE_DA_SAUDACAO));
    } catch {
        return SAUDACAO_PADRAO;
    }
};
